import classNames from 'classnames';
import React, { MouseEventHandler, useCallback } from 'react';
import { Link, LinkProps } from 'react-router-dom';

import Spinner from '../Spinner';

interface TextProps {
  ariaDescribedBy?: string;
  children: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  blurOnClick?: boolean;
  to?: LinkProps['to'];
  external?: boolean;
  variant?: 'primary' | 'secondary' | 'critical' | 'white';
  testId?: string;
  type?: 'button' | 'submit' | 'reset';
  truncate?: boolean;
}

const baseClassnames = ['relative', 'cursor-pointer'];

// Primary is a theme blue text link
const primaryClassnames = ['text-link', 'hover:text-link-hover', 'focus:text-link-hover'];

// Secondary is a black text link for light theme, white text link for dark theme
const secondaryClassnames = ['text-default', 'hover:opacity-75 focus:opacity-75'];

// Critical is a red text link
const criticalClassnames = [
  'text-alert-critical',
  'hover:text-alert-critical-hover',
  'focus:text-alert-critical-hover',
];

// Force white
const whiteClassnames = ['text-white dark:text-white hover:text-light-faint focus:text-dark-muted'];

export const textLinkVariantClassnames = {
  primary: primaryClassnames,
  secondary: secondaryClassnames,
  critical: criticalClassnames,
  white: whiteClassnames,
};

const TextLink = ({
  ariaDescribedBy,
  children,
  className,
  to,
  external,
  loading,
  onClick: onClickProp,
  blurOnClick = false,
  variant = 'primary',
  disabled,
  testId,
  type,
  truncate,
}: TextProps) => {
  const onClick = useCallback<MouseEventHandler<HTMLElement>>(
    (e) => {
      onClickProp?.(e);
      if (blurOnClick) e.currentTarget.blur();
    },
    [onClickProp, blurOnClick],
  );
  const themedClassnames = classNames(baseClassnames, textLinkVariantClassnames[variant], {
    'pointer-events-none select-none disabled': disabled,
  });
  const textContent = (
    <span className={classNames('relative', truncate && 'min-w-0 truncate', disabled && 'opacity-25')}>
      {children}
      {loading && (
        <span className="absolute top-[50%] translate-y-[-50%] right-0 translate-x-[175%]">
          <Spinner size="sm" />
        </span>
      )}
    </span>
  );
  if (onClick && !to)
    return (
      <button
        aria-describedby={ariaDescribedBy}
        data-testid={testId}
        className={classNames(themedClassnames, 'disabled:opacity-100', className)}
        disabled={disabled}
        onClick={onClick}
        type={type}
      >
        {textContent}
      </button>
    );
  else if (to && external)
    return (
      <a
        aria-describedby={ariaDescribedBy}
        className={classNames(themedClassnames, className)}
        href={typeof to === 'string' ? to : '#'}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        onClick={onClick}
      >
        {textContent}
      </a>
    );
  else if (to)
    return (
      <Link
        aria-describedby={ariaDescribedBy}
        className={classNames(themedClassnames, className)}
        to={to || '#'}
        data-testid={testId}
        onClick={onClick}
      >
        {textContent}
      </Link>
    );
  else return textContent;
};

export default React.memo(TextLink) as typeof TextLink;
